import type { AmendPayload, OrderItemResults, ResultValueInput } from "@probetalab/contracts";
import { annexFromDraft, type Draft, cultureKey, cultureOf, noteFromDraft, valuesFromDraft } from "../components/CaptureRows.js";

/**
 * Correcting a validated result from Captura (Fernanda, 2026-11-02 21:02; ADR 0022). The values
 * are edited in the same rows as a capture, and «Guardar corrección» sends `results.amend`, the
 * same command and the same server checks as any amendment: the original stays frozen, the
 * amendment records who and when, and the study is `amended` until it is validated again.
 *
 * ADR 0040: the *reason* is asked for only when the result has already left the laboratory — the
 * server says which it is in `correction_declared`, and an undeclared correction sends none and
 * prints nothing about being corrected.
 */

/** Shortest reason the screen accepts; the server only refuses an empty one. */
export const MIN_REASON = 3;

/** The study can be corrected: validated or issued (an amended one is already open for editing). */
export function canBeCorrected(r: Pick<OrderItemResults, "state">): boolean {
  return r.state === "validated" || r.state === "issued";
}

/**
 * The `results.amend` payload: only the values that differ from what was validated (an unchanged
 * value is not a correction), plus the note when it changed. Null when nothing changed.
 */
export function amendPayload(r: OrderItemResults, draft: Draft, baseline: Draft, reason: string): AmendPayload | null {
  const declared = r.correction_declared;
  const culture = cultureOf(r);
  const changed = (v: ResultValueInput): boolean => {
    if (v.clear) return true;
    const keys = [v.analyte_id, ...(culture?.document.analyte_id === v.analyte_id ? [cultureKey(v.analyte_id)] : [])];
    return keys.some((k) => (draft[k] ?? "").trim() !== (baseline[k] ?? "").trim());
  };
  const values = valuesFromDraft(r, draft).filter(changed);
  const note = noteFromDraft(r, draft);
  // ADR 0030: replacing or removing a validated attachment is a correction like any other.
  const annex = annexFromDraft(r, draft);
  if (!values.length && note === undefined && annex.attachments === undefined && annex.performed_by_lab === undefined) return null;
  return { order_item_id: r.order_item_id, ...(declared ? { reason: reason.trim() } : {}), values, ...(note !== undefined ? { note } : {}), ...annex };
}

/** Why «Guardar corrección» is not available yet, in the card's words; null when it is. */
export function correctionBlock(r: OrderItemResults, draft: Draft, baseline: Draft, reason: string): string | null {
  if (!amendPayload(r, draft, baseline, reason)) return "Cambia al menos un valor.";
  if (r.correction_declared && reason.trim().length < MIN_REASON) return "Escribe el motivo de la corrección.";
  return null;
}
